// src/pages/CharacterDetail.tsx (Updated with Sync Button)
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../services/authService';
import { getUserCharacters, Character } from '../services/characterService';
import { getMockCharacterById } from '../services/apiMockService';
import LoadingState from '../components/common/LoadingState';
import SyncProgressButton from '../components/achievements/SyncProgressButton';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';

const CharacterDetail: React.FC = () => {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [character, setCharacter] = useState<Character | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadCharacterData = useCallback(async () => {
    if (!isAuthenticated()) {
      setError('Authentication required');
      setLoading(false);
      return;
    }
  
    try {
      setLoading(true);
      
      // Lade Charakterdaten über die API
      const characters = await getUserCharacters();
      const foundCharacter = characters.find(c => c.characterId === id);
      
      if (foundCharacter) {
        setCharacter(foundCharacter);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Character not found in API response, trying mock data...');
          const mockCharacter = getMockCharacterById(id || '');
          if (mockCharacter) {
            console.log('Using mock character data:', mockCharacter);
            setCharacter(mockCharacter);
          } else {
            setError('Character not found');
          }
        } else {
          setError('Character not found');
        }
      }
    } catch (err: any) {
      console.error('Error fetching character details:', err);
      if (process.env.NODE_ENV === 'development') {
        console.log('Error fetching character, trying mock data...');
        const mockCharacter = getMockCharacterById(id || '');
        if (mockCharacter) {
          console.log('Using mock character data instead:', mockCharacter);
          setCharacter(mockCharacter);
        } else {
          setError(err.message || 'Failed to load character details');
        }
      } else {
        setError(err.message || 'Failed to load character details');
      }
    } finally {
      setLoading(false);
    }
  }, [id, setLoading, setError, setCharacter]); 

  useEffect(() => {
    loadCharacterData();
    
    // Listen for auth state changes
    const handleAuthChange = () => {
      console.log("Auth state change detected in CharacterDetail component");
      loadCharacterData();
    };
    
    window.addEventListener('auth-state-changed', handleAuthChange);
    
    return () => {
      window.removeEventListener('auth-state-changed', handleAuthChange);
    };
  }, [id,loadCharacterData]);

  const formatPlayTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days} days, ${hours % 24} hours, ${minutes % 60} minutes`;
    } else {
      return `${hours} hours, ${minutes % 60} minutes`;
    }
  };

  if (loading) {
    return <LoadingState message={t('common.loading')} />;
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-destiny-red mb-4">{t('common.error')}</h2>
        <p className="text-gray-300 mb-4">{error}</p>
        <Link 
          to="/characters" 
          className="inline-flex items-center text-destiny-blue hover:text-destiny-gold"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          {t('nav.backToCharacters')}
        </Link>
      </div>
    );
  }

  if (!character) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-destiny-red mb-4">{t('character.notFound')}</h2>
        <p className="text-gray-300 mb-4">{t('character.characterNotFound')}</p>
        <Link 
          to="/characters" 
          className="inline-flex items-center text-destiny-blue hover:text-destiny-gold"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          {t('nav.backToCharacters')}
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Back button */}
      <div className="flex justify-between items-center mb-6">
        <Link 
          to="/characters" 
          className="inline-flex items-center text-destiny-blue hover:text-destiny-gold"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          {t('nav.backToCharacters')}
        </Link>
        
        <SyncProgressButton 
          characterId={character.characterId}
          onSyncComplete={() => {
            // If we want to refresh character data after sync
            // For now, just show success message
          }}
        />
      </div>
      
      {/* Character Header */}
      <div 
        className="bg-gray-800 rounded-lg overflow-hidden mb-6"
        style={{
          backgroundImage: character.emblemBackgroundPath ? `url(${character.emblemBackgroundPath})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="bg-gray-900 bg-opacity-70 p-6">
          <div className="flex items-center">
            {character.emblemPath && (
              <img
                src={character.emblemPath}
                alt={`${character.className} emblem`}
                className="w-20 h-20 mr-6 rounded"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = '/placeholder-emblem.png';
                }}
              />
            )}
            <div>
              <h1 className="text-3xl font-bold text-destiny-gold">{character.className}</h1>
              <p className="text-xl text-gray-300 mt-1">{character.race} {character.gender}</p>
              <div className="mt-2 inline-flex items-center bg-destiny-dark bg-opacity-80 px-3 py-1 rounded">
                <span className="text-destiny-gold font-bold text-lg">✧ {character.light}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Character Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">{t('character.information')}</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">{t('character.class')}:</span>
              <span className="text-white">{character.className}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t('character.race')}:</span>
              <span className="text-white">{character.race}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t('character.gender')}:</span>
              <span className="text-white">{character.gender}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t('character.level')}:</span>
              <span className="text-white">{character.level}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t('character.lightLevel')}:</span>
              <span className="text-destiny-gold font-bold">{character.light}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t('character.lastPlayed')}:</span>
              <span className="text-white">{new Date(character.lastPlayed).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t('character.totalPlayTime')}:</span>
              <span className="text-white">{formatPlayTime(character.minutesPlayedTotal)}</span>
            </div>
          </div>
        </div>
        
        {/* Character Actions */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">{t('common.actions')}</h2>
          
          <div className="grid grid-cols-1 gap-4">
            <Link 
              to={`/achievements?character=${character.characterId}`}
              className="bg-destiny-blue hover:bg-opacity-90 text-white py-3 px-4 rounded text-center font-medium"
            >
              {t('character.viewAchievements')}
            </Link>
            
            <button
              onClick={() => navigate(`/achievements?character=${character.characterId}`)}
              className="bg-gray-700 hover:bg-gray-600 text-white py-3 px-4 rounded text-center font-medium"
            >
              {t('character.manageProgress')}
            </button>
            
            <a 
              href={`https://www.bungie.net/en/Profile/254/Characters/${character.characterId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-700 hover:bg-gray-600 text-white py-3 px-4 rounded text-center font-medium"
            >
              {t('character.viewOnBungie')}
            </a>
          </div>
          
          <div className="mt-6 bg-gray-700 rounded-lg p-4">
            <h3 className="font-medium text-destiny-gold mb-2">{t('character.characterProgress')}</h3>
            <p className="text-gray-300 text-sm">
              {t('character.progressDescription')}
            </p>
            <div className="mt-3 text-center">
              <Link
                to={`/achievements?character=${character.characterId}&sync=1`}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-destiny-green hover:bg-opacity-90"
              >
                {t('character.viewProgressDetails')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterDetail;